import { inject, Injectable } from '@angular/core';
import {
  ChannelNotification,
  NotificationRule,
  NotificationSlugEnum,
} from '@app/generated/graphql';
import {
  INotificationConfig,
  INotificationMessageConfig,
  INotificationPreferenceConfig,
} from '../interfaces/interface';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class NotificationPreferencesConfigService {
  // dependencies
  private _router = inject(Router);

  // constants
  readonly DEFAULT_MESSAGE_LIFE = 8000;
  private readonly _NOTIFICATION_CONFIG: Partial<
    Record<NotificationSlugEnum, INotificationConfig>
  > = {
    [NotificationSlugEnum.Example]: {
      slug: NotificationSlugEnum.Example,
      preference: {
        displayTitle: 'Example',
        displayDescription: 'Example',
        defaultConfig: {},
      },
      message: {
        iconPath: '/assets/icons/general/info.svg',
        displayTitle: 'Example',
        displayDescription: 'Example',
        actionLabel: 'View',
        action: (_metadata: Record<string, unknown>) => {},
        life: this.DEFAULT_MESSAGE_LIFE,
      },
    },
    [NotificationSlugEnum.ClientChangeRequest]: (() => {
      const displayTitle: string = 'Client change request';
      const displayDescription: string =
        'A masterfile setup request has been updated.';

      return {
        slug: NotificationSlugEnum.ClientChangeRequest,
        preference: {
          displayTitle:
            'A new masterfile setup I have requested has been activated',
          displayDescription:
            'Get notified when requests for new data setups for packhouses, farms, exporters and coldstores have been actioned.',
          defaultChannels: ['notify_web'],
          defaultConfig: {},
        },
        message: {
          iconPath: '/assets/icons/general/info.svg',
          displayTitle,
          displayDescription,
          resolveDisplayTitle: (
            metadata: Record<string, unknown>,
          ): string | undefined => {
            switch (metadata['status']) {
              case 'approved':
                return 'Requested Masterfile Setup approved';
              case 'rejected':
                return 'Requested Masterfile Setup rejected';
              default:
                return displayTitle;
            }
          },
          resolveDisplayDescription: (
            metadata: Record<string, unknown>,
          ): string | undefined => {
            const requestType = metadata['request_type'] as string | undefined;
            const packhouseName = metadata['packhouse_name'] as
              | string
              | undefined;
            const exporterName = metadata['exporter_name'] as
              | string
              | undefined;

            switch (requestType) {
              case 'internal_packhouse':
                return packhouseName
                  ? `Setups for requested packhouse ${packhouseName} complete.`
                  : displayDescription;
              case 'external_packhouse':
                return packhouseName
                  ? `Setups for requested external packhouse ${packhouseName} complete.`
                  : displayDescription;
              case 'company_association_for_existing_company':
              case 'company_association_for_missing_company':
                return exporterName
                  ? `Setups requested for exporter ${exporterName} complete.`
                  : displayDescription;
              default:
                return displayDescription;
            }
          },
          actionLabel: 'View',
          action: (metadata: Record<string, unknown>) => {
            const requestType: string = metadata['request_type'] as string;
            if (!requestType) {
              return;
            }

            switch (requestType) {
              case 'internal_packhouse':
              case 'external_packhouse':
                this._router.navigate([
                  '/account/masterfiles/packhouse-manager',
                ]);
                break;
              case 'company_association_for_existing_company':
              case 'company_association_for_missing_company':
                this._router.navigate([
                  '/account/masterfiles/supply-chain-setup',
                ]);
                break;
            }
          },
          life: this.DEFAULT_MESSAGE_LIFE,
        },
      };
    })(),
  } as const;

  getNotificationConfig(slug: NotificationSlugEnum): INotificationConfig {
    return this._NOTIFICATION_CONFIG[slug];
  }

  getNotificationPreferenceConfig(
    slug: NotificationSlugEnum,
  ): INotificationPreferenceConfig {
    return this._NOTIFICATION_CONFIG[slug].preference;
  }

  getNotificationMessageConfig(
    slug: NotificationSlugEnum,
  ): INotificationMessageConfig {
    return this._NOTIFICATION_CONFIG[slug].message;
  }

  /**
   * Resolved title/body for any channel notification (toast, drawers).
   * Uses optional `resolveDisplayTitle` / `resolveDisplayDescription` on the message config when present.
   */
  getResolvedChannelNotificationCopy(notification: ChannelNotification): {
    title: string;
    body: string;
  } {
    const rule: NotificationRule | undefined = notification.notificationRule;
    const slug: NotificationSlugEnum | undefined = rule?.notificationSlug;
    const metadata: Record<string, unknown> =
      this._channelMetadataRecord(notification.channelMetadata) ?? {};
    const messageConfig: INotificationMessageConfig | undefined =
      slug != null ? this._NOTIFICATION_CONFIG[slug]?.message : undefined;

    const resolvedTitle: string | undefined =
      messageConfig?.resolveDisplayTitle?.(metadata);
    const resolvedBody: string | undefined =
      messageConfig?.resolveDisplayDescription?.(metadata);

    const title: string =
      resolvedTitle ??
      notification.title ??
      rule?.name ??
      messageConfig?.displayTitle ??
      '';

    const body: string =
      resolvedBody ??
      notification.body ??
      messageConfig?.displayDescription ??
      rule?.description ??
      '';

    return { title, body };
  }

  private _channelMetadataRecord(
    metadata: ChannelNotification['channelMetadata'],
  ): Record<string, unknown> | undefined {
    if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
      return metadata as Record<string, unknown>;
    }

    return undefined;
  }
}
